import { useMemo } from 'react';
import { Spin, Typography } from 'antd';
import { CodeOutlined } from '@ant-design/icons';

import { useGetDocumentDetailsByIdQuery } from '@/store/apis/documentsApi';
import JsonViewer from '@/pages/mortgage-workflow/utils/json-viewer.utils';

import { DetailPane } from './DocumentViewer';

const { Text } = Typography;

/**
 * The OCR pane, for a document that lives in Copilot's own document table.
 *
 * ⚠️ Only for those. The id is the key to `documentDetails`, so a file stored
 * anywhere else - a document-invite blob, say - has no row to find and the pane
 * would sit there empty. Those callers pass no detail at all.
 *
 * Returns `undefined` when there is nothing to show, which is also how a caller
 * turns the pane off: `useOcrDetail(id, false)`.
 */
export const useOcrDetail = (
  documentId?: string | null,
  enabled = true,
): DetailPane | undefined => {
  const { data, isLoading } = useGetDocumentDetailsByIdQuery(
    { documentId: documentId as string },
    { skip: !documentId || !enabled },
  );

  const metadata = useMemo(
    () => data?.data?.diNormalized?.response?.ocrResult?.data ?? null,
    [data],
  );

  return useMemo(() => {
    if (!documentId || !enabled) return undefined;

    return {
      label: 'OCR Result',
      icon: (
        <CodeOutlined style={{ color: metadata ? '#1677ff' : '#8c8c8c' }} />
      ),
      note: metadata ? `${Object.keys(metadata).length} fields` : undefined,
      content: isLoading ? (
        <div style={{ textAlign: 'center', paddingTop: 30 }}>
          <Spin size="small" tip="Parsing OCR Data..." />
        </div>
      ) : metadata ? (
        <JsonViewer data={metadata} />
      ) : (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <Text type="secondary" italic style={{ fontSize: 12 }}>
            No OCR metadata extracted for this document.
          </Text>
        </div>
      ),
    };
  }, [documentId, enabled, isLoading, metadata]);
};
